import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TrendingUp, TrendingDown, Wallet, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function Money() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjLabel, setAdjLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/shop/balance").then((r) => setBalance(r.data));
    api.get("/shop/ledger").then((r) => setLedger(r.data));
  };

  useEffect(() => {
    if (user.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [user.role, navigate]);

  if (user.role !== "admin") return null;

  const adjust = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/shop/ledger/adjust", { amount: Number(adjAmount), label: adjLabel });
      toast.success("Shop money adjusted");
      setAdjustOpen(false); setAdjAmount(""); setAdjLabel("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.delete("/shop/ledger");
      toast.success("Money in/out reset to zero");
      setResetOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="money-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-primary mb-2 flex items-center gap-3">
            <span className="h-px w-8 bg-primary/60" />
            SHOP VAULT <span className="font-jp text-muted-foreground">金庫</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight uppercase">Money Control</h1>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="adjust-money-btn"
            onClick={() => setAdjustOpen(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-widest rounded-sm h-11 px-5"
          >
            <Wallet size={14} className="mr-2" /> ADJUST MONEY
          </Button>
          <Button
            data-testid="reset-money-btn"
            onClick={() => setResetOpen(true)}
            variant="outline"
            className="border-destructive/50 text-destructive hover:bg-destructive/10 font-display text-[11px] tracking-widest rounded-sm h-11 px-5"
          >
            <RotateCcw size={14} className="mr-2" /> RESET
          </Button>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="grid sm:grid-cols-3 gap-4 mb-6" data-testid="money-strip">
        <div className="bg-card border border-primary/30 rounded-md p-5 shadow-[0_0_30px_rgba(0,255,102,0.07)]">
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.25em] text-muted-foreground">SHOP MONEY</span>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
          </div>
          <div className={`font-display text-3xl font-black mt-2 ${balance && balance.balance < 0 ? "text-destructive" : "text-primary neon-glow"}`} data-testid="shop-balance">
            {balance ? fmt(balance.balance) : "—"}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1.5">live — updates as sales & purchases happen</div>
        </div>
        <div className="bg-card border border-border rounded-md p-5">
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground flex items-center gap-1.5"><TrendingUp size={12} className="text-emerald-400" /> MONEY IN</span>
          <div className="font-display text-2xl font-black text-emerald-400 mt-2" data-testid="money-in">{balance ? fmt(balance.money_in) : "—"}</div>
        </div>
        <div className="bg-card border border-border rounded-md p-5">
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground flex items-center gap-1.5"><TrendingDown size={12} className="text-amber-400" /> MONEY OUT</span>
          <div className="font-display text-2xl font-black text-amber-400 mt-2" data-testid="money-out">{balance ? fmt(balance.money_out) : "—"}</div>
        </div>
      </motion.div>

      <div className="bg-card border border-border rounded-md p-5" data-testid="money-ledger">
        <span className="text-[10px] tracking-[0.25em] text-muted-foreground">FULL MONEY LEDGER</span>
        <div className="mt-3 space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {ledger.map((e) => (
            <div key={e.id} className="flex items-center justify-between text-xs border-b border-border/40 pb-2" data-testid={`ledger-${e.id}`}>
              <span className="text-muted-foreground truncate mr-3">
                <span className="text-foreground">{e.label}</span>
                <span className="ml-2 text-[10px]">{new Date(e.created_at).toLocaleString()}</span>
              </span>
              <span className={`font-display font-bold shrink-0 ${e.amount >= 0 ? "text-emerald-400" : "text-amber-400"}`}>
                {e.amount >= 0 ? "+" : "−"}{fmt(Math.abs(e.amount))}
              </span>
            </div>
          ))}
          {!ledger.length && <div className="text-muted-foreground text-xs py-10 text-center" data-testid="ledger-empty">Ledger is empty — money movement appears here as it happens.</div>}
        </div>
      </div>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-sm" data-testid="adjust-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">ADJUST SHOP MONEY</DialogTitle>
          </DialogHeader>
          <form onSubmit={adjust} className="space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">AMOUNT $ (NEGATIVE TO TAKE OUT)</label>
              <Input data-testid="adjust-amount-input" type="number" step="1" required value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder="e.g. 500000 or -25000" className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">REASON</label>
              <Input data-testid="adjust-label-input" required value={adjLabel} onChange={(e) => setAdjLabel(e.target.value)} placeholder="e.g. Starting cash / Bank deposit" className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <Button data-testid="adjust-confirm-btn" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.25em] h-12 rounded-sm">
              {busy ? "APPLYING…" : "APPLY ADJUSTMENT"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#0C100D] border-destructive/40 text-foreground max-w-sm" data-testid="reset-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide text-destructive">RESET MONEY LEDGER</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground leading-relaxed">
            This wipes every money movement and sets Money In, Money Out and Shop Money to{" "}
            <span className="text-foreground font-semibold">$0</span>. Sales records and commissions are
            NOT touched. This cannot be undone.
          </p>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setResetOpen(false)} data-testid="reset-cancel-btn" className="flex-1 border-border rounded-sm">
              CANCEL
            </Button>
            <Button onClick={reset} disabled={busy} data-testid="reset-confirm-btn" className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-sm font-display text-[11px] tracking-widest">
              {busy ? "RESETTING…" : "RESET TO $0"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
